import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import DatePicker, { todayStr } from "@/components/DatePicker";
import { formatHour } from "@/lib/utils";
import { X, Flame } from "lucide-react";

function isPastSlot(hour, dateStr) {
  if (!dateStr) return false;
  const today = new Date().toISOString().split("T")[0];
  if (dateStr !== today) return false;
  const now = new Date();
  return hour < now.getHours();
}

export function InstantBookingModal({ courts, onClose, onCreated }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState("manual"); // "manual" | "slots"
  const [courtId, setCourtId] = useState("");
  const [date, setDate] = useState(todayStr());
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [slots, setSlots] = useState([]);
  const [pickedHours, setPickedHours] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [customPrice, setCustomPrice] = useState("");
  const [playerName, setPlayerName] = useState("");
  const [playerPhone, setPlayerPhone] = useState("");
  const [playerEmail, setPlayerEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const selectedCourt = courts.find((c) => c.id === courtId);
  const duration = selectedCourt ? (selectedCourt.default_slot_duration || selectedCourt.slot_duration || 60) : 60;
  const [selectedDuration, setSelectedDuration] = useState(duration);
  const step = selectedDuration / 60;
  const durationOptions = selectedCourt?.slot_pricing
    ? Object.keys(selectedCourt.slot_pricing).map(Number).sort((a, b) => a - b)
    : [duration];

  useEffect(() => {
    const d = selectedCourt
      ? (selectedCourt.default_slot_duration || selectedCourt.slot_duration || 60)
      : 60;
    setSelectedDuration(d);
  }, [selectedCourt]);

  // Load slots when court or date changes in slot mode
  useEffect(() => {
    if (mode !== "slots" || !courtId || !date) return;
    setSlotsLoading(true);
    setSlots([]);
    setPickedHours([]);
    api.get(`/courts/${courtId}/availability`, { params: { date, duration: selectedDuration } })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [courtId, date, mode, selectedDuration]);

  // Reset picked hours when court or duration changes in slot mode
  useEffect(() => {
    if (mode === "slots") setPickedHours([]);
  }, [courtId, selectedDuration, mode]);

  const toggleSlot = (h, available) => {
    if (!available || isPastSlot(h, date)) return;
    setPickedHours((prev) => {
      if (prev.includes(h)) return prev.filter((x) => x !== h);
      const next = [...prev, h].sort((a, b) => a - b);
      for (let i = 1; i < next.length; i++) {
        if (Math.abs(next[i] - next[i - 1] - step) > 0.001) return [h];
      }
      return next;
    });
  };

  const timeToDecimal = (t) => {
    if (!t) return 0;
    const [h, m] = t.split(":").map(Number);
    return h + m / 60;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!courtId || !date || !playerName || !playerPhone) {
      setError("Please fill in all required fields.");
      return;
    }

    let start_hour, end_hour;

    if (mode === "manual") {
      if (!startTime || !endTime) {
        setError("Please fill in start and end times.");
        return;
      }
      start_hour = timeToDecimal(startTime);
      end_hour = timeToDecimal(endTime);
      if (end_hour <= start_hour) {
        setError("End time must be after start time.");
        return;
      }
    } else {
      if (pickedHours.length === 0) {
        setError("Please select at least one time slot.");
        return;
      }
      start_hour = pickedHours[0];
      end_hour = pickedHours[pickedHours.length - 1] + step;
    }

    setSubmitting(true);
    try {
      const payload = {
        court_id: courtId,
        date,
        start_hour,
        end_hour,
        payment_method: "cod",
        user_name: playerName,
        user_phone: playerPhone,
        user_email: playerEmail || undefined,
      };
      if (customPrice !== "" && !isNaN(Number(customPrice))) {
        payload.custom_price = Number(customPrice);
      }
      const r = await api.post("/bookings", payload);
      onCreated(r.data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Failed to create booking.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-card border border-border w-full max-w-md rounded-sm shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div>
            <div className="text-volt text-sm tracking-widest uppercase font-bold">{t("owner.instant_booking")}</div>
            <div className="text-muted-foreground text-xs mt-0.5">{t("owner.instant_booking_desc")}</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors p-1">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Mode toggle */}
        <div className="flex border-b border-border shrink-0">
          <button
            type="button"
            onClick={() => setMode("manual")}
            className={`flex-1 py-2.5 text-[10px] tracking-widest uppercase font-bold transition-colors ${
              mode === "manual"
                ? "text-volt border-b-2 border-volt bg-volt/5"
                : "text-muted-foreground hover:text-foreground border-b-2 border-transparent"
            }`}
          >
            {t("owner.mode_manual")}
          </button>
          <button
            type="button"
            onClick={() => setMode("slots")}
            className={`flex-1 py-2.5 text-[10px] tracking-widest uppercase font-bold transition-colors ${
              mode === "slots"
                ? "text-volt border-b-2 border-volt bg-volt/5"
                : "text-muted-foreground hover:text-foreground border-b-2 border-transparent"
            }`}
          >
            {t("owner.mode_slots")}
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4 overflow-y-auto flex-1 min-h-0">

          {/* Court */}
          <div>
            <label className="block text-[10px] tracking-widest uppercase font-semibold mb-1.5">{t("owner.select_court")} *</label>
            <select
              value={courtId}
              onChange={(e) => { setCourtId(e.target.value); setStartTime(""); setEndTime(""); }}
              className="w-full border border-border bg-background text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-volt"
              required
            >
              <option value="">— {t("owner.select_court")} —</option>
              {courts.filter((c) => c.active).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Date — always visible */}
          <div>
            <label className="block text-[10px] tracking-widest uppercase font-semibold mb-1.5">{t("owner.select_date")} *</label>
            <DatePicker value={date} onChange={setDate} min={todayStr()} />
          </div>

          {/* ─── QUICK MANUAL MODE ─── */}
          {mode === "manual" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] tracking-widest uppercase font-semibold mb-1.5">{t("owner.select_time")} *</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  step="900"
                  className="w-full border border-border bg-background text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-volt"
                  required
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase font-semibold mb-1.5">{t("owner.select_time")} *</label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  step="900"
                  className="w-full border border-border bg-background text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-volt"
                  required
                />
              </div>
            </div>
          )}

          {/* ─── SLOT PICKER MODE ─── */}
          {mode === "slots" && (
            <div>
              <label className="block text-[10px] tracking-widest uppercase font-semibold mb-1.5">{t("owner.pick_slots_hint")}</label>
              {durationOptions.length > 1 && (
                <div className="flex gap-1.5 mb-3">
                  {durationOptions.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setSelectedDuration(d)}
                      className={`text-[10px] px-2.5 py-1 border font-mono tracking-wider transition-colors ${
                        selectedDuration === d
                          ? "border-volt bg-volt text-black font-bold"
                          : "border-border text-muted-foreground hover:border-foreground/40"
                      }`}
                    >
                      {d}m
                    </button>
                  ))}
                </div>
              )}
              {slotsLoading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground/70">
                  <span className="text-[10px] tracking-widest uppercase animate-pulse">{t("common.loading")}</span>
                </div>
              ) : slots.length === 0 ? (
                <div className="border border-dashed border-border/60 bg-background/40 p-4 text-center text-xs text-muted-foreground/80">
                  {t("owner.no_slots_available")}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-1.5 max-h-52 overflow-auto pr-1">
                  {slots.map((s) => {
                    const picked = pickedHours.includes(s.hour);
                    const discountActive = s.has_discount && s.original_price > s.price;
                    const hasWeekendPeak = selectedCourt?.peak_weekend;
                    return (
                      <button
                        key={s.hour}
                        type="button"
                        disabled={!s.available}
                        onClick={() => toggleSlot(s.hour, s.available)}
                        className={`py-2 text-[10px] font-mono border transition-all relative ${
                          !s.available
                            ? "border-red-400/20 bg-[repeating-linear-gradient(45deg,transparent,transparent_3px,rgba(248,113,113,0.06)_3px,rgba(248,113,113,0.06)_6px)] text-muted-foreground/60 cursor-not-allowed"
                            : picked
                            ? "border-volt bg-volt text-primary-foreground font-bold"
                            : "border-border hover:border-foreground/40"
                        }`}
                      >
                        {formatHour(s.hour)}
                        {(s.is_peak || (hasWeekendPeak && s.is_weekend)) && (
                          <Flame className="absolute top-0.5 end-0.5 h-2.5 w-2.5 text-volt opacity-80" />
                        )}
                        {discountActive && s.available && (
                          <span className="absolute top-0.5 start-0.5 bg-volt text-primary-foreground text-[7px] leading-none px-0.5 py-[1px] font-bold">
                            -{Math.round((1 - s.price / s.original_price) * 100)}%
                          </span>
                        )}
                        {!s.available && (
                          <span className="absolute top-0.5 start-0.5 bg-red-400/80 text-white text-[7px] leading-none px-1 py-[1px] font-bold">
                            {t("owner.slot_unavailable")}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {pickedHours.length > 0 && (
                <div className="mt-2 text-[10px] text-muted-foreground text-center">
                  {pickedHours.length} slot{pickedHours.length > 1 ? "s" : ""} selected ·{" "}
                  {formatHour(pickedHours[0])} – {formatHour(pickedHours[pickedHours.length - 1] + step)}
                </div>
              )}
            </div>
          )}

          {/* Custom Price */}
          <div>
            <label className="block text-[10px] tracking-widest uppercase font-semibold mb-1.5">{t("owner.custom_price")} (JD)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={customPrice}
              onChange={(e) => setCustomPrice(e.target.value)}
              placeholder={t("owner.custom_price_placeholder")}
              className="w-full border border-border bg-background text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-volt"
            />
            <p className="text-[10px] text-muted-foreground mt-1">{t("owner.custom_price_hint")}</p>
          </div>

          {/* Player name & phone */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] tracking-widest uppercase font-semibold mb-1.5">{t("owner.player_name")} *</label>
              <input
                type="text"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="e.g. Ahmad"
                className="w-full border border-border bg-background text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-volt"
                required
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-widest uppercase font-semibold mb-1.5">{t("owner.player_phone")} *</label>
              <input
                type="tel"
                value={playerPhone}
                onChange={(e) => setPlayerPhone(e.target.value)}
                placeholder="07X XXX XXXX"
                className="w-full border border-border bg-background text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-volt"
                required
              />
            </div>
          </div>

          {/* Player email */}
          <div>
            <label className="block text-[10px] tracking-widest uppercase font-semibold mb-1.5">{t("owner.player_email")}</label>
            <input
              type="email"
              value={playerEmail}
              onChange={(e) => setPlayerEmail(e.target.value)}
              placeholder="player@example.com"
              className="w-full border border-border bg-background text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-volt"
            />
          </div>

          {error && (
            <div className="border border-red-400/30 bg-red-400/5 text-red-400 text-xs px-3 py-2.5 rounded-sm">{error}</div>
          )}

          <div className="flex gap-3 pt-2 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-border text-foreground/80 px-4 py-2.5 text-xs tracking-widest uppercase font-bold hover:bg-muted transition-colors"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-volt text-black font-bold text-xs tracking-widest uppercase px-4 py-2.5 hover:bg-volt/90 transition-colors disabled:opacity-50"
            >
              {submitting ? t("owner.booking") : t("owner.create_booking")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}